// src/Pages/PatientDashboard/Feedback.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Rating,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Star as StarIcon,
  RateReview as RateReviewIcon,
  History as HistoryIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { 
  fetchFeedbackHistory, 
  submitFeedback,
  clearSubmitError,
  clearSubmitSuccess,
  resetFeedbackState 
} from '../../features/feedback/feedbackSlice';
import { useFeatureFlag } from '../../config/featureFlags';

const Feedback = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  
  // Feature flags
  const isFeedbackEnabled = useFeatureFlag('FEATURE_PATIENT_FEEDBACK');
  
  // Redux state
  const { history, submitting, submitError, submitSuccess } = useSelector((state) => state.feedback);
  
  // Local state
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [appointmentId, setAppointmentId] = useState('');

  useEffect(() => {
    dispatch(fetchFeedbackHistory());
  }, [dispatch]);

  useEffect(() => {
    if (submitSuccess) {
      toast.success('Feedback submitted successfully!');
      setFeedbackDialogOpen(false);
      resetForm();
      dispatch(clearSubmitSuccess());
    }
  }, [submitSuccess, dispatch]);

  useEffect(() => {
    if (submitError) {
      toast.error(submitError);
      dispatch(clearSubmitError());
    }
  }, [submitError, dispatch]);

  const resetForm = () => {
    setRating(0);
    setComment('');
    setAppointmentId('');
    setSelectedDoctor(null);
  };

  const handleSubmitFeedback = async () => {
    if (!selectedDoctor || rating === 0) {
      toast.error('Please select a doctor and provide a rating');
      return;
    }

    const feedbackData = {
      rating,
      comment: comment.trim(),
      appointment_id: appointmentId || null
    };

    try {
      await dispatch(submitFeedback({ doctorId: selectedDoctor.id, feedbackData })).unwrap();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const handleOpenFeedbackDialog = (doctor) => {
    setSelectedDoctor(doctor);
    setFeedbackDialogOpen(true);
  };

  const handleCloseFeedbackDialog = () => {
    setFeedbackDialogOpen(false);
    resetForm();
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <StarIcon
        key={index}
        sx={{
          color: index < rating ? theme.palette.warning.main : theme.palette.grey[300],
          fontSize: 20,
        }}
      />
    ));
  };

  if (!isFeedbackEnabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Feedback feature is currently not available.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Feedback & Reviews
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Reviews
                  </Typography>
                  <Typography variant="h4">
                    {history.data.length}
                  </Typography>
                </Box>
                <RateReviewIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Average Rating
                  </Typography>
                  <Typography variant="h4">
                    {history.data.length > 0
                      ? (history.data.reduce((sum, item) => sum + item.rating, 0) / history.data.length).toFixed(1)
                      : '0.0'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex' }}>
                  {renderStars(Math.round(history.data.length > 0
                    ? history.data.reduce((sum, item) => sum + item.rating, 0) / history.data.length
                    : 0))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    5-Star Reviews
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {history.data.filter(item => item.rating === 5).length}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex' }}>
                  {renderStars(5)}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Recent Reviews
                  </Typography>
                  <Typography variant="h4">
                    {history.data.filter(item => {
                      const reviewDate = new Date(item.created_at);
                      const thirtyDaysAgo = new Date();
                      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                      return reviewDate > thirtyDaysAgo;
                    }).length}
                  </Typography>
                </Box>
                <HistoryIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Feedback History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Your Feedback History
          </Typography>

          {history.loading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 3 }).map((_, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Skeleton variant="rectangular" height={80} />
                </Box>
              ))}
            </Box>
          ) : history.error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {history.error}
            </Alert>
          ) : history.data.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                No feedback submitted yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Share your experience with doctors to help others
              </Typography>
            </Box>
          ) : (
            <List>
              {history.data.map((feedback, index) => (
                <React.Fragment key={feedback.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar>{feedback.doctor.name.charAt(0)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {feedback.doctor.name}
                          </Typography>
                          {feedback.doctor.specialization && (
                            <Chip
                              label={feedback.doctor.specialization}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {renderStars(feedback.rating)}
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {feedback.rating}/5
                            </Typography>
                          </Box>
                          {feedback.comment && (
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                              "{feedback.comment}"
                            </Typography>
                          )}
                          <Typography variant="caption" color="textSecondary">
                            Submitted on {format(new Date(feedback.created_at), 'MMM dd, yyyy')}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < history.data.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Submit New Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={handleCloseFeedbackDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Submit Feedback
          {selectedDoctor && (
            <Typography variant="body2" color="textSecondary">
              for {selectedDoctor.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Rate your experience
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating
                    value={rating}
                    onChange={(_, newValue) => setRating(newValue)}
                    size="large"
                  />
                  <Typography variant="body2" color="textSecondary">
                    {rating}/5
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Your feedback (optional)"
                  placeholder="Share your experience with this doctor..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Appointment (optional)</InputLabel>
                  <Select
                    value={appointmentId}
                    label="Appointment (optional)"
                    onChange={(e) => setAppointmentId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>No specific appointment</em>
                    </MenuItem>
                    {/* TODO: Add appointment options */}
                    <MenuItem value="appointment_1">Appointment #1 - Jan 15, 2024</MenuItem>
                    <MenuItem value="appointment_2">Appointment #2 - Jan 10, 2024</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeedbackDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitFeedback}
            disabled={submitting || rating === 0}
            startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Feedback Button */}
      <Box sx={{ position: 'fixed', bottom: 24, right: 24 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<RateReviewIcon />}
          onClick={() => {
            // TODO: Show doctor selection dialog
            const mockDoctor = {
              id: '1',
              name: 'Dr. Anil Sharma',
              specialization: 'Psychiatrist'
            };
            handleOpenFeedbackDialog(mockDoctor);
          }}
          sx={{
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          Submit Feedback
        </Button>
      </Box>
    </Box>
  );
};

export default Feedback;
